import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { ArrowLeft, Loader2, DollarSign, TrendingUp } from 'lucide-react';
import { formatAmount, getStatusColor } from './utils/formatters';

export function DatasetFeeReport() {
  const { datasetId } = useParams();
  const [dataset, setDataset] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (datasetId) {
      loadDatasetData();
    }
  }, [datasetId]);

  const loadDatasetData = async () => {
    try {
      const [datasetResult, statsResult, bankStatsResult] = await Promise.all([
        supabase
          .from('datasets')
          .select('*')
          .eq('id', datasetId)
          .single(),
        supabase
          .rpc('get_dataset_fee_report', { dataset_id_param: datasetId }),
        supabase
          .rpc('get_dataset_bank_stats', { dataset_id_param: datasetId })
      ]);

      if (datasetResult.error) throw datasetResult.error;
      if (statsResult.error) throw statsResult.error;
      if (bankStatsResult.error) throw bankStatsResult.error;

      setDataset(datasetResult.data);
      setStats({
        ...statsResult.data,
        bankStats: bankStatsResult.data
      });
    } catch (err) {
      console.error('Error loading dataset data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dataset data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error || !dataset || !stats) {
    return (
      <div className="text-center py-12">
        <div className="card p-6 max-w-md mx-auto">
          <p className="text-[14px] text-red-600">{error || 'Dataset not found'}</p>
        </div>
      </div>
    );
  }

  const netRevenue = stats.totalSiteFees - stats.totalBankFees - stats.totalResellerFees;

  return (
    <div className="space-y-6">
      <Link to={`/datasets/${datasetId}`} className="back-link">
        <ArrowLeft className="w-4 h-4" />
        Back to Dataset
      </Link>

      <div className="page-header">
        <div>
          <h1 className="page-title">Fee Report</h1>
          <p className="page-subtitle">{dataset.name} &middot; Created {new Date(dataset.created_at).toLocaleDateString()}</p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-violet-600" />
            </div>
            <span className="stat-label">Total Volume</span>
          </div>
          <p className="stat-value">{formatAmount(stats.totalAmount)}</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
            </div>
            <span className="stat-label">Net Revenue</span>
          </div>
          <p className="text-2xl font-bold text-emerald-600 mt-1.5">{formatAmount(netRevenue)}</p>
          <div className="mt-3 space-y-1.5 text-[13px]">
            <div className="flex justify-between text-slate-500">
              <span>Site Fees</span>
              <span className="font-medium text-slate-700">{formatAmount(stats.totalSiteFees)}</span>
            </div>
            <div className="flex justify-between text-slate-500">
              <span>Bank Fees</span>
              <span className="font-medium text-slate-700">-{formatAmount(stats.totalBankFees)}</span>
            </div>
            <div className="flex justify-between text-slate-500">
              <span>Reseller Fees</span>
              <span className="font-medium text-slate-700">-{formatAmount(stats.totalResellerFees)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bank Summary */}
      <div>
        <h2 className="section-title mb-4">Bank Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(stats.bankStats || {}).map(([bank, bankData]: [string, any]) => {
            const bankNet = bankData.total_site_fees - bankData.total_bank_fees - bankData.total_reseller_fees;
            return (
              <div key={bank} className="card p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-[15px] font-semibold text-slate-900">{bank}</h3>
                  <span className="badge-green">{formatAmount(bankNet)}</span>
                </div>

                <div className="flex justify-between items-baseline mb-4">
                  <span className="text-[13px] text-slate-500">Volume</span>
                  <div className="text-right">
                    <span className="text-[15px] font-semibold text-slate-900">{formatAmount(bankData.volume)}</span>
                    <span className="text-[11px] text-slate-400 ml-1">
                      ({((bankData.volume / stats.totalAmount) * 100).toFixed(1)}%)
                    </span>
                    <div className="text-[12px] text-slate-400">
                      {bankData.count.toLocaleString()} txns
                    </div>
                  </div>
                </div>

                <div className="pt-3 border-t border-slate-100 space-y-1.5 text-[13px]">
                  <div className="flex justify-between text-slate-500">
                    <span>Site Fees</span>
                    <span className="font-medium text-slate-700">{formatAmount(bankData.total_site_fees)}</span>
                  </div>
                  <div className="flex justify-between text-slate-500">
                    <span>Bank Fees</span>
                    <span className="font-medium text-slate-700">-{formatAmount(bankData.total_bank_fees)}</span>
                  </div>
                  <div className="flex justify-between text-slate-500">
                    <span>Reseller Fees</span>
                    <span className="font-medium text-slate-700">-{formatAmount(bankData.total_reseller_fees)}</span>
                  </div>
                </div>

                {/* Status dots */}
                <div className="mt-4 pt-3 border-t border-slate-100">
                  <p className="text-[12px] font-medium text-slate-400 uppercase tracking-wide mb-2">Status</p>
                  <div className="space-y-1.5">
                    {Object.entries(Object.entries(bankData.types || {}).reduce((acc: any, [type, typeData]: [string, any]) => {
                      Object.entries(typeData.methods || {}).forEach(([method, methodData]: [string, any]) => {
                        Object.entries(methodData.statuses || {}).forEach(([status, statusData]: [string, any]) => {
                          if (!acc[status]) acc[status] = { count: 0, volume: 0 };
                          acc[status].count += statusData.count;
                          acc[status].volume += statusData.volume;
                        });
                      });
                      return acc;
                    }, {})).map(([status, data]: [string, any]) => (
                      <div key={status} className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <span className={`w-1.5 h-1.5 rounded-full ${getStatusColor(status)}`} />
                          <span className="text-[13px] text-slate-600 capitalize">{status}</span>
                        </div>
                        <div className="text-right text-[13px]">
                          <span className="font-medium text-slate-700">{data.count.toLocaleString()}</span>
                          <span className="text-slate-400 ml-1">({((data.count / bankData.count) * 100).toFixed(1)}%)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bank Revenue Breakdown */}
      <div>
        <h2 className="section-title mb-4">Revenue Breakdown by Type</h2>
        <div className="space-y-4">
          {Object.entries(stats.bankStats || {}).map(([bank, bankData]: [string, any]) => (
            <div key={bank} className="card">
              <div className="p-5 border-b border-slate-100">
                <h3 className="text-[15px] font-semibold text-slate-900">{bank}</h3>
              </div>
              <div className="p-5 space-y-4">
                {Object.entries(bankData.types || {}).map(([type, typeData]: [string, any]) => (
                  <div key={type}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[14px] font-semibold text-slate-800">{type}</span>
                      <span className="text-[13px] text-slate-500">
                        {formatAmount(typeData.volume)} &middot; {((typeData.count / bankData.count) * 100).toFixed(1)}%
                      </span>
                    </div>

                    <div className="space-y-2">
                      {Object.entries(typeData.methods || {}).map(([method, methodData]: [string, any]) => (
                        <div key={method} className="pl-4 border-l-2 border-slate-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[13px] font-medium text-slate-600">{method}</span>
                            <span className="text-[12px] text-slate-400">
                              {formatAmount(methodData.volume)}
                            </span>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {Object.entries(methodData.statuses || {}).map(([status, statusData]: [string, any]) => {
                              const statusNet = statusData.total_site_fees - statusData.total_bank_fees - statusData.total_reseller_fees;
                              return (
                                <div key={status} className="bg-slate-50 rounded-lg p-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-1.5">
                                      <span className={`w-1.5 h-1.5 rounded-full ${getStatusColor(status)}`} />
                                      <span className="text-[12px] font-medium text-slate-700 capitalize">{status}</span>
                                    </div>
                                    <span className="text-[12px] text-slate-400">
                                      {statusData.count.toLocaleString()} txns
                                    </span>
                                  </div>
                                  <div className="space-y-1 text-[12px]">
                                    <div className="flex justify-between text-slate-500">
                                      <span>Volume</span>
                                      <span className="font-medium text-slate-700">{formatAmount(statusData.volume)}</span>
                                    </div>
                                    <div className="flex justify-between text-slate-500">
                                      <span>Site</span>
                                      <span>{formatAmount(statusData.total_site_fees)}</span>
                                    </div>
                                    <div className="flex justify-between text-slate-500">
                                      <span>Bank</span>
                                      <span>-{formatAmount(statusData.total_bank_fees)}</span>
                                    </div>
                                    <div className="flex justify-between text-slate-500">
                                      <span>Reseller</span>
                                      <span>-{formatAmount(statusData.total_reseller_fees)}</span>
                                    </div>
                                    <div className="pt-1 mt-1 border-t border-slate-200 flex justify-between font-medium">
                                      <span className="text-slate-700">Net</span>
                                      <span className="text-emerald-600">{formatAmount(statusNet)}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Status Distribution */}
      <div>
        <h2 className="section-title mb-4">Status Distribution</h2>
        <div className="card p-5 space-y-5">
          {Object.entries(stats.typeStats || {}).map(([type, typeData]: [string, any]) => (
            <div key={type}>
              <h3 className="text-[14px] font-semibold text-slate-800 mb-3">{type}</h3>
              <div className="space-y-4">
                {Object.entries(typeData.methods || {}).map(([method, methodData]: [string, any]) => (
                  <div key={method} className="pl-4 border-l-2 border-slate-200">
                    <h4 className="text-[13px] font-medium text-slate-600 mb-2">{method}</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {Object.entries(methodData.statuses || {}).map(([status, statusData]: [string, any]) => {
                        const siteFees = statusData.total_site_fees || 0;
                        const bankFees = statusData.total_bank_fees || 0;
                        const resellerFees = statusData.total_reseller_fees || 0;
                        const totalFees = siteFees - bankFees - resellerFees;

                        return (
                          <div key={status} className="bg-slate-50 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-1.5">
                                <span className={`w-1.5 h-1.5 rounded-full ${getStatusColor(status)}`} />
                                <span className="text-[12px] font-medium text-slate-700 capitalize">{status}</span>
                              </div>
                              <span className="text-[12px] text-slate-400">{statusData.count} txns</span>
                            </div>
                            <div className="space-y-1 text-[12px]">
                              <div className="flex justify-between">
                                <span className="text-slate-500">Volume</span>
                                <span className="font-medium text-slate-700">{formatAmount(statusData.volume)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Revenue</span>
                                <span className="font-medium text-emerald-600">{formatAmount(totalFees)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default DatasetFeeReport;
